import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase/config';

interface DatabaseConfig {
  name: string;
  database: string;
  displayName: string;
  description: string;
}

interface DatabaseContextType {
  currentDatabase: string;
  availableDatabases: DatabaseConfig[];
  switchDatabase: (databaseName: string) => void;
  client: typeof supabase; // Supabase client instance
}

const AVAILABLE_DATABASES: DatabaseConfig[] = [
  {
    name: 'production',
    database: 'production',
    displayName: 'Production Database',
    description: 'Live production data - use with caution'
  },
  {
    name: 'development',
    database: 'development',
    displayName: 'Development Database', 
    description: 'Safe development environment for testing'
  }
];

const DatabaseContext = createContext<DatabaseContextType | undefined>(undefined);

export const DatabaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentDatabase, setCurrentDatabase] = useState<string>('production');

  // Load saved database preference from localStorage
  useEffect(() => {
    const savedDatabase = localStorage.getItem('selectedDatabase');
    console.log('🔍 DatabaseContext: Loading saved database from localStorage:', savedDatabase);
    if (savedDatabase && AVAILABLE_DATABASES.some(db => db.name === savedDatabase)) {
      console.log('🔍 DatabaseContext: Setting currentDatabase to:', savedDatabase);
      setCurrentDatabase(savedDatabase);
    } else {
      console.log('🔍 DatabaseContext: Using default database: production');
    }
  }, []);

  // Initialize database connection when currentDatabase changes
  useEffect(() => {
    console.log('🔍 DatabaseContext: Initializing database for:', currentDatabase);
    const selectedConfig = AVAILABLE_DATABASES.find(config => config.name === currentDatabase);
    if (selectedConfig) {
      try {
        console.log('🔍 DatabaseContext: Selected config:', selectedConfig);
        console.log(`🔄 Database set to: ${selectedConfig.displayName}`);
        console.log('🔍 DatabaseContext: Supabase client ready');
      } catch (error) {
        console.error('Error setting database context:', error);
        // Fallback to production database
        setCurrentDatabase('production');
      }
    }
  }, [currentDatabase]);

  const switchDatabase = (databaseName: string) => {
    if (AVAILABLE_DATABASES.some(db => db.name === databaseName)) {
      setCurrentDatabase(databaseName);
      localStorage.setItem('selectedDatabase', databaseName);
      
      // Show notification about database switch
      const config = AVAILABLE_DATABASES.find(db => db.name === databaseName);
      if (config) {
        console.log(`🔄 Switching to ${config.displayName}`);
        // For Supabase, we don't need to reload the page since it's all the same database
        // but we can use different schemas or table prefixes if needed in the future
      }
    }
  };

  const contextValue: DatabaseContextType = {
    currentDatabase,
    availableDatabases: AVAILABLE_DATABASES,
    switchDatabase,
    client: supabase // Provide the Supabase client
  };

  return (
    <DatabaseContext.Provider value={contextValue}>
      {children}
    </DatabaseContext.Provider>
  );
};

export const useDatabase = (): DatabaseContextType => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
};

// Helper function to get the Supabase client
export const getSupabaseClient = () => {
  return supabase;
};